import React, { useState, useEffect, useRef, useMemo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Race } from '../../types';
import { formatTime, formatDate, formatOdds, isNonRunner } from '../../utils/helpers';
import { 
  Trophy, 
  BarChart3, 
  Target, 
  Check, 
  X, 
  Ban, 
  TrendingUp, 
  TrendingDown,
  Percent,
  Hash
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CircularProgress from '../CircularProgress';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import SimpleSlider from '../SimpleSlider';
import '../ResultsHistory.css';
import type { TrackRecordChartData, TrackRecordSummary } from '../../services/trackRecordService';

export interface ResultsHistoryViewProps {
  races: Race[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  header?: ReactNode;
  footerBanner?: ReactNode;
  showSubscriberLinks?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasMoreRemote?: boolean;
  totalRemoteCount?: number;
  /** Full-dataset summary (e.g. public track record); tiles ignore table filters and loaded page count. */
  globalSummary?: TrackRecordSummary | null;
  /** Full-dataset chart series; charts ignore table filters and loaded page count. */
  globalChartData?: TrackRecordChartData | null;
}

interface ScoreFilters {
  overall: [number, number];
  horse: [number, number];
  jockey: [number, number];
  trainer: [number, number];
  owner: [number, number];
}

const ResultsHistoryView: React.FC<ResultsHistoryViewProps> = ({
  races,
  loading,
  error,
  onRetry,
  header,
  footerBanner,
  showSubscriberLinks = true,
  onLoadMore,
  loadingMore = false,
  hasMoreRemote = false,
  totalRemoteCount,
  globalSummary = null,
  globalChartData = null,
}) => {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const courseDropdownRef = useRef<HTMLDivElement>(null);
  const raceTypeDropdownRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Filter states
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState<string[]>([]);
  const [raceTypeFilter, setRaceTypeFilter] = useState<string[]>([]);
  const [surfaceFilter, setSurfaceFilter] = useState('');
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [raceTypeDropdownOpen, setRaceTypeDropdownOpen] = useState(false);
  const [runnersFilter, setRunnersFilter] = useState<[number, number]>([0, 0]);
  const [stakeFilter, setStakeFilter] = useState<[number, number]>([1, 3]);
  const [scoreFilters, setScoreFilters] = useState<ScoreFilters>({
    overall: [0, 100],
    horse: [0, 100],
    jockey: [0, 100],
    trainer: [0, 100],
    owner: [0, 100]
  });
  const [visibleCount, setVisibleCount] = useState(50); // Number of races to show initially

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target as Node)) {
        setCourseDropdownOpen(false);
      }
      if (raceTypeDropdownRef.current && !raceTypeDropdownRef.current.contains(event.target as Node)) {
        setRaceTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set initial score filter ranges based on actual data
  useEffect(() => {
    if (races.length > 0) {
      setScoreFilters({
        overall: getScoreRange('ai_overall_score'),
        horse: getScoreRange('ai_horse_score'),
        jockey: getScoreRange('ai_jockey_score'),
        trainer: getScoreRange('ai_trainer_score'),
        owner: getScoreRange('ai_owner_score')
      });
      
      // Set initial runners filter range based on actual data
      const runners = races.map(race => race.field_size || 0).filter(size => size > 0);
      if (runners.length > 0) {
        const minRunners = Math.min(...runners);
        const maxRunners = Math.max(...runners);
        // Only set if not already initialized (initial state is [0, 0])
        if (runnersFilter[0] === 0 && runnersFilter[1] === 0) {
          setRunnersFilter([minRunners, maxRunners]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [races]);

  const getResultStatus = (raceResult: string | null, aiSelection: string, nonRunner?: string): 'WON' | 'LOST' | 'VOID' | 'PENDING' => {
    if (!raceResult) return 'PENDING';
    
    // Check if it's a non-runner first
    if (isNonRunner(aiSelection, nonRunner)) return 'VOID';
    
    // Normalize names for comparison (remove brackets)
    const normalizeName = (name: string) => name.replace(/\s*\([^)]*\)\s*/g, '').trim();
    const normalizedResult = normalizeName(raceResult);
    const normalizedSelection = normalizeName(aiSelection);
    
    return normalizedResult === normalizedSelection ? 'WON' : 'LOST';
  };

  const getResultColor = (status: 'WON' | 'LOST' | 'VOID' | 'PENDING'): string => {
    switch (status) {
      case 'WON': return '#059669';
      case 'LOST': return '#dc2626';
      case 'VOID': return '#6b7280';
      default: return '#d97706';
    }
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (key: keyof Race) => {
    const values = races.map(race => race[key]).filter((value): value is string => 
      typeof value === 'string' && value.trim() !== ''
    );
    return Array.from(new Set(values)).sort();
  };

  // Get score range for sliders
  const getScoreRange = (key: keyof Race): [number, number] => {
    const scores = races.map(race => race[key] as number).filter(score => typeof score === 'number' && !isNaN(score));
    if (scores.length === 0) return [0, 100];
    return [Math.min(...scores), Math.max(...scores)] as [number, number];
  };

  // Get runners range from actual data
  const getRunnersRange = (): [number, number] => {
    const runners = races.map(race => race.field_size || 0).filter(size => size > 0);
    if (runners.length === 0) return [0, 30];
    return [Math.min(...runners), Math.max(...runners)];
  };

  // Update score filter
  const updateScoreFilter = (scoreType: keyof ScoreFilters, value: [number, number]) => {
    setScoreFilters(prev => ({
      ...prev,
      [scoreType]: value
    }));
  };

  // Handle multi-select dropdown changes
  const toggleCourse = (course: string) => {
    setCourseFilter(prev => 
      prev.includes(course) 
        ? prev.filter(c => c !== course)
        : [...prev, course]
    );
  };

  const toggleAllCourses = () => {
    const allCourses = getUniqueValues('course');
    if (courseFilter.length === allCourses.length) {
      setCourseFilter([]);
    } else {
      setCourseFilter(allCourses);
    }
  };

  const toggleRaceType = (raceType: string) => {
    setRaceTypeFilter(prev => 
      prev.includes(raceType) 
        ? prev.filter(rt => rt !== raceType)
        : [...prev, raceType]
    );
  };

  const toggleAllRaceTypes = () => {
    const allRaceTypes = getUniqueValues('race_type');
    if (raceTypeFilter.length === allRaceTypes.length) {
      setRaceTypeFilter([]);
    } else {
      setRaceTypeFilter(allRaceTypes);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setDateFromFilter('');
    setDateToFilter('');
    setResultFilter('');
    setCourseFilter([]);
    setRaceTypeFilter([]);
    setSurfaceFilter('');
    setStakeFilter([1, 3]);
    
    // Reset runners filter to full range
    const runnersRange = getRunnersRange();
    setRunnersFilter(runnersRange);
    
    // Reset score filters to full range
    if (races.length > 0) {
      setScoreFilters({
        overall: getScoreRange('ai_overall_score'),
        horse: getScoreRange('ai_horse_score'),
        jockey: getScoreRange('ai_jockey_score'),
        trainer: getScoreRange('ai_trainer_score'),
        owner: getScoreRange('ai_owner_score')
      });
    }
  };

  // Filter races based on current filter state
  const filteredRaces = races.filter(race => {
    // Date filter (from/to range)
    if (dateFromFilter && race.race_date && race.race_date < dateFromFilter) return false;
    if (dateToFilter && race.race_date && race.race_date > dateToFilter) return false;
    
    // Result filter
    if (resultFilter) {
      const resultStatus = getResultStatus(race.race_result, race.ai_selection, race.non_runner);
      if (resultStatus !== resultFilter) return false;
    }
    
    // Course filter (multi-select)
    if (courseFilter.length > 0 && !courseFilter.includes(race.course)) return false;
    
    // Race type filter (multi-select)
    if (raceTypeFilter.length > 0 && !raceTypeFilter.includes(race.race_type)) return false;
    
    // Surface filter
    if (surfaceFilter && race.surface !== surfaceFilter) return false;
    
    // Runners filter
    const runners = race.field_size || 0;
    if (runners < runnersFilter[0] || runners > runnersFilter[1]) return false;
    
    // Stake filter
    const stake = parseFloat(race.suggested_stake || '0');
    if (stake < stakeFilter[0] || stake > stakeFilter[1]) return false;
    
    // Score filters
    if (race.ai_overall_score < scoreFilters.overall[0] || race.ai_overall_score > scoreFilters.overall[1]) return false;
    if (race.ai_horse_score < scoreFilters.horse[0] || race.ai_horse_score > scoreFilters.horse[1]) return false;
    if (race.ai_jockey_score < scoreFilters.jockey[0] || race.ai_jockey_score > scoreFilters.jockey[1]) return false;
    if (race.ai_trainer_score < scoreFilters.trainer[0] || race.ai_trainer_score > scoreFilters.trainer[1]) return false;
    if (race.ai_owner_score < scoreFilters.owner[0] || race.ai_owner_score > scoreFilters.owner[1]) return false;
    
    return true;
  });

  // Infinite scroll: load more races when scrolling near bottom
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Load more when user is within 200px of bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        if (onLoadMore && hasMoreRemote && !loadingMore) {
          onLoadMore();
          return;
        }
        setVisibleCount(prev => {
          if (prev < filteredRaces.length) {
            return Math.min(prev + 50, filteredRaces.length);
          }
          return prev;
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [filteredRaces.length, onLoadMore, hasMoreRemote, loadingMore]);

  // Parse a race's decimal odds defensively. Returns null when odds are
  // missing, zero, <=1 or non-numeric. Guards against a subtle bug where
  // `parseFloat(race.bet365_decimal_odds || '0')` yields 0 for a winner,
  // which makes `stake * (0 - 1) = -stake` book a win as a full-stake loss.
  const parseWinOdds = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) && n > 1 ? n : null;
  };

  // Calculate daily P/L and cumulative P/L for charts (loaded races only)
  const computedChartData = useMemo(() => {
    if (filteredRaces.length === 0) return { daily: [], cumulative: [] };

    // Group races by date and calculate daily P/L
    const dailyPLMap = new Map<string, number>();
    
    filteredRaces.forEach(race => {
      if (!race.race_date) return;
      
      const date = race.race_date;
      const result = getResultStatus(race.race_result, race.ai_selection, race.non_runner);
      const stake = parseFloat(race.suggested_stake || '0');

      let pl = 0;
      if (result === 'WON') {
        const odds = parseWinOdds(race.bet365_decimal_odds);
        if (odds === null) {
          // Winner with no usable odds - skip entirely rather than
          // booking `stake * (0 - 1) = -stake` as a phantom loss.
          return;
        }
        pl = stake * (odds - 1);
      } else if (result === 'LOST') {
        pl = -stake; // Loss
      }
      // VOID races don't affect P/L (stake returned)

      const currentPL = dailyPLMap.get(date) || 0;
      dailyPLMap.set(date, currentPL + pl);
    });

    // Convert to array and sort by date
    const dailyData = Array.from(dailyPLMap.entries())
      .map(([date, pl]) => ({
        date: formatDate(date),
        dateKey: date, // Keep original for sorting
        pl: parseFloat(pl.toFixed(2))
      }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));

    // Calculate cumulative P/L
    let cumulativePL = 0;
    const cumulativeData = dailyData.map(item => {
      cumulativePL += item.pl;
      return {
        date: item.date,
        cumulativePL: parseFloat(cumulativePL.toFixed(2))
      };
    });

    return { daily: dailyData, cumulative: cumulativeData };
  }, [filteredRaces]);

  const chartData = globalChartData ?? computedChartData;

  // Calculate summary statistics (from full dataset when globalSummary is provided)
  let skippedWinsCount = 0;
  const {
    totalRaces,
    totalStake,
    averageOdds,
    wonRaces,
    lostRaces,
    voidRaces,
    winRate,
    totalPL,
    roi,
  } = globalSummary
    ? {
        totalRaces: globalSummary.totalRaces,
        totalStake: globalSummary.totalStake,
        averageOdds: globalSummary.averageOdds,
        wonRaces: globalSummary.wonRaces,
        lostRaces: globalSummary.lostRaces,
        voidRaces: globalSummary.voidRaces,
        winRate: globalSummary.winRate,
        totalPL: globalSummary.totalPL,
        roi: globalSummary.roi,
      }
    : (() => {
        const totalRacesLocal = filteredRaces.length;
        const totalStakeLocal = filteredRaces.reduce(
          (sum, race) => sum + parseFloat(race.suggested_stake || '0'),
          0
        );
        const averageOddsLocal =
          filteredRaces.length > 0
            ? filteredRaces.reduce(
                (sum, race) => sum + parseFloat(race.bet365_decimal_odds || '0'),
                0
              ) / filteredRaces.length
            : 0;

        const wonRacesLocal = filteredRaces.filter(
          (race) =>
            getResultStatus(race.race_result, race.ai_selection, race.non_runner) === 'WON'
        ).length;
        const lostRacesLocal = filteredRaces.filter(
          (race) =>
            getResultStatus(race.race_result, race.ai_selection, race.non_runner) === 'LOST'
        ).length;
        const voidRacesLocal = filteredRaces.filter(
          (race) =>
            getResultStatus(race.race_result, race.ai_selection, race.non_runner) === 'VOID'
        ).length;
        const winRateLocal = totalRacesLocal > 0 ? (wonRacesLocal / totalRacesLocal) * 100 : 0;

        let skipped = 0;
        const totalPLLocal = filteredRaces.reduce((sum, race) => {
          const result = getResultStatus(race.race_result, race.ai_selection, race.non_runner);
          const stake = parseFloat(race.suggested_stake || '0');

          if (result === 'WON') {
            const odds = parseWinOdds(race.bet365_decimal_odds);
            if (odds === null) {
              skipped += 1;
              return sum;
            }
            return sum + stake * (odds - 1);
          }
          if (result === 'LOST') {
            return sum - stake;
          }
          return sum;
        }, 0);

        skippedWinsCount = skipped;
        const roiLocal = totalStakeLocal > 0 ? (totalPLLocal / totalStakeLocal) * 100 : 0;

        return {
          totalRaces: totalRacesLocal,
          totalStake: totalStakeLocal,
          averageOdds: averageOddsLocal,
          wonRaces: wonRacesLocal,
          lostRaces: lostRacesLocal,
          voidRaces: voidRacesLocal,
          winRate: winRateLocal,
          totalPL: totalPLLocal,
          roi: roiLocal,
        };
      })();

  if (!globalSummary && skippedWinsCount > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ResultsHistory] Skipped ${skippedWinsCount} winning race(s) from P/L because their odds were missing or invalid.`
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={onRetry} />;
  }

  if (races.length === 0) {
    return (
      <div className="results-history">
      </div>
    );
  }

  const defaultHeader = (
    <div className="results-header">
      <h1>Results History</h1>
      <p>
        This page shows a complete history of all the picks and results from our system.
        {showSubscriberLinks && (
          <>
            {' '}
            To view how your bets have performed, visit{' '}
            <Link to="/performance" className="results-header-link">
              Performance
            </Link>
            .
          </>
        )}
      </p>
    </div>
  );

  return (
    <div className="results-history">
      {footerBanner}
      {header ?? defaultHeader}

      {/* Summary Statistics */}
      <div className="results-summary-stats">
        <div className="summary-tile">
          <div className="summary-icon">
            <Trophy size={20} />
          </div>
          <div className="summary-value">{totalRaces}</div>
          <div className="summary-label">Races</div>
        </div>
        
        <div className="summary-tile">
          <div className="summary-icon">
            <Hash size={20} />
          </div>
          <div className="summary-value">{totalStake.toFixed(2)}</div>
          <div className="summary-label">Total Stake (pts)</div>
        </div>
        
        <div className="summary-tile">
          <div className="summary-icon">
            <BarChart3 size={20} />
          </div>
          <div className="summary-value">{averageOdds.toFixed(2)}</div>
          <div className="summary-label">Average Odds</div>
        </div>
        
        <div className="summary-tile">
          <div className="summary-icon">
            <Target size={20} />
          </div>
          <div className="summary-value">{winRate.toFixed(1)}%</div>
          <div className="summary-label">Win Rate</div>
        </div>
        
        <div className="summary-tile won">
          <div className="summary-icon">
            <Check size={20} />
          </div>
          <div className="summary-value">{wonRaces}</div>
          <div className="summary-label">Won</div>
        </div>
        
        <div className="summary-tile lost">
          <div className="summary-icon">
            <X size={20} />
          </div>
          <div className="summary-value">{lostRaces}</div>
          <div className="summary-label">Lost</div>
        </div>
        
        <div className="summary-tile void">
          <div className="summary-icon">
            <Ban size={20} />
          </div>
          <div className="summary-value">{voidRaces}</div>
          <div className="summary-label">Void</div>
        </div>
        
        <div className={`summary-tile ${totalPL >= 0 ? 'profit' : 'loss'}`}>
          <div className="summary-icon">
            {totalPL >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
          </div>
          <div className="summary-value">{totalPL >= 0 ? '+' : ''}{totalPL.toFixed(2)}</div>
          <div className="summary-label">P/L</div>
        </div>
        
        <div className={`summary-tile ${roi >= 0 ? 'profit' : 'loss'}`}>
          <div className="summary-icon">
            {roi >= 0 ? <Percent size={20} /> : <TrendingDown size={20} />}
          </div>
          <div className="summary-value">{roi >= 0 ? '+' : ''}{roi.toFixed(1)}%</div>
          <div className="summary-label">ROI</div>
        </div>
      </div>

      {/* P/L Charts Section */}
      {chartData.daily.length > 0 && (
        <div className="pl-charts-section">
          <div className="pl-chart-container">
            <h3 className="pl-chart-title">Daily P/L</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                <XAxis 
                  dataKey="date" 
                  stroke="#cbd5e1"
                  tick={{ fill: '#cbd5e1', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#cbd5e1"
                  tick={{ fill: '#cbd5e1', fontSize: 12 }}
                  tickFormatter={(value) => Number(value).toFixed(2)}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #475569',
                    borderRadius: '6px',
                    color: '#f1f5f9'
                  }}
                  formatter={(value: any) => Number(value).toFixed(2)}
                />
                <Legend 
                  wrapperStyle={{ color: '#cbd5e1' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="pl" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  name="Daily P/L (pts)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div className="pl-chart-container">
            <h3 className="pl-chart-title">Cumulative P/L</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.cumulative}>
                <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                <XAxis 
                  dataKey="date" 
                  stroke="#cbd5e1"
                  tick={{ fill: '#cbd5e1', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#cbd5e1"
                  tick={{ fill: '#cbd5e1', fontSize: 12 }}
                  tickFormatter={(value) => Number(value).toFixed(2)}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #475569',
                    borderRadius: '6px',
                    color: '#f1f5f9'
                  }}
                  formatter={(value: any) => Number(value).toFixed(2)}
                />
                <Legend 
                  wrapperStyle={{ color: '#cbd5e1' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="cumulativePL" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  dot={{ fill: '#22c55e', r: 4 }}
                  name="Cumulative P/L (pts)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header">
          <h3>Filters</h3>
          <div className="filters-header-actions">
            <button 
              className="reset-filters-btn"
              onClick={resetFilters}
              title="Reset all filters"
            >
              Reset
            </button>
            <button 
              className="filter-toggle"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              {filtersExpanded ? '−' : '+'}
            </button>
          </div>
        </div>
        
        {filtersExpanded && (
          <div className="filters-content">
            <div className="filter-row">
              <div className="filter-group">
                <label>Date From</label>
                <select
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="">All Dates</option>
                  {getUniqueValues('race_date').map(date => (
                    <option key={date} value={date}>{formatDate(date)}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Date To</label>
                <select
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="">All Dates</option>
                  {getUniqueValues('race_date').map(date => (
                    <option key={date} value={date}>{formatDate(date)}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group filter-group-narrow">
                <label>Result</label>
                <select 
                  value={resultFilter} 
                  onChange={(e) => setResultFilter(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="">All Results</option>
                  <option value="WON">Won</option>
                  <option value="LOST">Lost</option>
                  <option value="VOID">Void</option>
                </select>
              </div>
              
              <div className="filter-group filter-group-wide filter-group-course">
                <label>Course</label>
                <div className="custom-multi-select" ref={courseDropdownRef}>
                  <button
                    type="button"
                    className="custom-multi-select-button"
                    onClick={() => setCourseDropdownOpen(!courseDropdownOpen)}
                  >
                    {courseFilter.length === 0 
                      ? 'All Courses' 
                      : `${courseFilter.length} selected`}
                    <span className="dropdown-arrow">{courseDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {courseDropdownOpen && (
                    <div className="custom-multi-select-menu">
                      <label className="custom-multi-select-item">
                        <input
                          type="checkbox"
                          checked={courseFilter.length === getUniqueValues('course').length}
                          onChange={toggleAllCourses}
                        />
                        <span>All Courses</span>
                      </label>
                      <div className="custom-multi-select-divider"></div>
                      {getUniqueValues('course').map(course => (
                        <label key={course} className="custom-multi-select-item">
                          <input
                            type="checkbox"
                            checked={courseFilter.includes(course)}
                            onChange={() => toggleCourse(course)}
                          />
                          <span>{course}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="filter-group filter-group-wide">
                <label>Race Type</label>
                <div className="custom-multi-select" ref={raceTypeDropdownRef}>
                  <button
                    type="button"
                    className="custom-multi-select-button"
                    onClick={() => setRaceTypeDropdownOpen(!raceTypeDropdownOpen)}
                  >
                    {raceTypeFilter.length === 0 
                      ? 'All Race Types' 
                      : `${raceTypeFilter.length} selected`}
                    <span className="dropdown-arrow">{raceTypeDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {raceTypeDropdownOpen && (
                    <div className="custom-multi-select-menu">
                      <label className="custom-multi-select-item">
                        <input
                          type="checkbox"
                          checked={raceTypeFilter.length === getUniqueValues('race_type').length}
                          onChange={toggleAllRaceTypes}
                        />
                        <span>All Race Types</span>
                      </label>
                      <div className="custom-multi-select-divider"></div>
                      {getUniqueValues('race_type').map(raceType => (
                        <label key={raceType} className="custom-multi-select-item">
                          <input
                            type="checkbox"
                            checked={raceTypeFilter.includes(raceType)}
                            onChange={() => toggleRaceType(raceType)}
                          />
                          <span>{raceType}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="filter-group filter-group-narrow">
                <label>Surface</label>
                <select 
                  value={surfaceFilter} 
                  onChange={(e) => setSurfaceFilter(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="">All Surfaces</option>
                  {getUniqueValues('surface').map(surface => (
                    <option key={surface} value={surface}>{surface}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <SimpleSlider
                  label="Stake"
                  value={stakeFilter}
                  min={1}
                  max={3}
                  step={0.25}
                  onChange={setStakeFilter}
                />
              </div>
              
              <div className="filter-group">
                <SimpleSlider
                  label="Number of Runners"
                  value={runnersFilter}
                  min={getRunnersRange()[0]}
                  max={getRunnersRange()[1]}
                  step={1}
                  onChange={setRunnersFilter}
                />
              </div>
            </div>
            
            <div className="filter-row">
              <div className="score-filters">
                <h4>AI Score Filters</h4>
                <div className="score-sliders">
                  <SimpleSlider
                    label="Overall Score"
                    value={scoreFilters.overall}
                    min={getScoreRange('ai_overall_score')[0]}
                    max={getScoreRange('ai_overall_score')[1]}
                    onChange={(value) => updateScoreFilter('overall', value)}
                  />
                  <SimpleSlider
                    label="Horse Score"
                    value={scoreFilters.horse}
                    min={getScoreRange('ai_horse_score')[0]}
                    max={getScoreRange('ai_horse_score')[1]}
                    onChange={(value) => updateScoreFilter('horse', value)}
                  />
                  <SimpleSlider
                    label="Jockey Score"
                    value={scoreFilters.jockey}
                    min={getScoreRange('ai_jockey_score')[0]}
                    max={getScoreRange('ai_jockey_score')[1]}
                    onChange={(value) => updateScoreFilter('jockey', value)}
                  />
                  <SimpleSlider
                    label="Trainer Score"
                    value={scoreFilters.trainer}
                    min={getScoreRange('ai_trainer_score')[0]}
                    max={getScoreRange('ai_trainer_score')[1]}
                    onChange={(value) => updateScoreFilter('trainer', value)}
                  />
                  <SimpleSlider
                    label="Owner Score"
                    value={scoreFilters.owner}
                    min={getScoreRange('ai_owner_score')[0]}
                    max={getScoreRange('ai_owner_score')[1]}
                    onChange={(value) => updateScoreFilter('owner', value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="results-table-container" ref={tableContainerRef}>
        <table className="results-table">
          <thead>
            <tr>
              <th>Result</th>
              <th>AI Selection</th>
              <th>Stake</th>
              <th>Odds</th>
              <th>Overall</th>
              <th>Horse</th>
              <th>Jockey</th>
              <th>Trainer</th>
              <th>Owner</th>
              <th>Course</th>
              <th>Race Type</th>
              <th>Surface</th>
              <th>Runners</th>
              <th>Distance</th>
              <th>Time</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredRaces.slice(0, visibleCount).map((race) => {
              const resultStatus = getResultStatus(race.race_result, race.ai_selection, race.non_runner);
              
              return (
                <tr key={race.race_id}>
                  <td className="result-cell">
                    <span 
                      className="result-badge"
                      style={{ color: getResultColor(resultStatus) }}
                    >
                      {resultStatus}
                    </span>
                  </td>
                  <td className="selection-cell">
                    {race.ai_selection.replace(/\s*\([^)]*\)\s*$/, '').trim()}
                  </td>
                  <td className="stake-cell">
                    {race.suggested_stake ? parseFloat(race.suggested_stake).toFixed(2) : 'N/A'}
                  </td>
                  <td className="odds-cell">
                    {race.bet365_decimal_odds ? formatOdds(parseFloat(race.bet365_decimal_odds)) : 'N/A'}
                  </td>
                  <td className="score-cell">
                    <CircularProgress 
                      score={race.ai_overall_score} 
                      label="" 
                      size={40}
                      strokeWidth={3}
                    />
                  </td>
                  <td className="score-cell">
                    <CircularProgress 
                      score={race.ai_horse_score} 
                      label="" 
                      size={40}
                      strokeWidth={3}
                    />
                  </td>
                  <td className="score-cell">
                    <CircularProgress 
                      score={race.ai_jockey_score} 
                      label="" 
                      size={40}
                      strokeWidth={3}
                    />
                  </td>
                  <td className="score-cell">
                    <CircularProgress 
                      score={race.ai_trainer_score} 
                      label="" 
                      size={40}
                      strokeWidth={3}
                    />
                  </td>
                  <td className="score-cell">
                    <CircularProgress 
                      score={race.ai_owner_score} 
                      label="" 
                      size={40}
                      strokeWidth={3}
                    />
                  </td>
                  <td className="course-cell">
                    {race.course}
                  </td>
                  <td className="race-type-cell">
                    {race.race_type}
                  </td>
                  <td className="surface-cell">
                    {race.surface}
                  </td>
                  <td className="runners-cell">
                    {race.field_size || 'N/A'}
                  </td>
                  <td className="distance-cell">
                    {race.distance}
                  </td>
                  <td className="time-cell">
                    {formatTime(race.race_time)}
                  </td>
                  <td className="date-cell">
                    {formatDate(race.race_date)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(visibleCount < filteredRaces.length || (onLoadMore && hasMoreRemote)) && (
          <div className="load-more-indicator">
            <p>
              Showing {Math.min(visibleCount, filteredRaces.length)} of{' '}
              {totalRemoteCount ?? filteredRaces.length} races.
              {loadingMore ? ' Loading more…' : ' Scroll down to load more...'}
            </p>
          </div>
        )}
        {filteredRaces.length > 0 &&
          visibleCount >= filteredRaces.length &&
          !(onLoadMore && hasMoreRemote) && (
          <div className="load-more-indicator">
            <p>All {filteredRaces.length} races displayed</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsHistoryView;
